import { FaultData, StatusData } from "@fairfleet/geotab";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Grid from "@mui/material/Grid2";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { useGridApiContext } from "@mui/x-data-grid-pro";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DeviceStatusOverviewData } from "../../models/DeviceStatusOverviewData";
import Cockpit from "./rowDetail/cockpit/Cockpit";
import CompactDiagnostics from "./rowDetail/CompactDiagnostics";
import CustomDeviceProperties from "./rowDetail/CustomDeviceProperties";
import DeviceDetail from "./rowDetail/DeviceDetail";
import DeviceHistory from "./rowDetail/DeviceHistory";
import DraggablePanelGrid from "./rowDetail/DraggablePanelGrid";
import FaultInstructions from "./rowDetail/faultDetail/FaultInstructions";
import FaultTable from "./rowDetail/faultDetail/FaultTable";
import GroupsDetail from "./rowDetail/GroupsDetail";
import MapComponent from "./rowDetail/MapComponent";
import TellTalesComponent from "./rowDetail/tellTaleDetail/TellTalesComponent";
import { COCKPIT_DIAGNOSTICS, TELLTALES_DIAGNOSTICS } from "@/constants/constants";
import { tellTales } from "@/constants/TellTales";
import { useRowDetailLayout } from "@/hooks/addins/useRowDetailLayout";
import { useLatestFaultData } from "@/hooks/faultData/useLatestFaultData";
import { useHasPermission } from "@/hooks/groups/useHasPermission";
import { useStatusInfoById } from "@/hooks/statusInfos/useStatusInfoById";

const DEFAULT_PANEL_ORDER = [
  "cockpit",
  "map",
  "tellTales",
  "tabs",
  "faultTable",
  "faultInstructions",
];

const RowDetailCockpit = React.memo(function RowDetailCockpit(props: {
  row: DeviceStatusOverviewData;
  fromDate?: Date;
  toDate?: Date;
}) {
  const deviceId = props.row.device.id;
  const deviceGroupIds = useMemo(
    () => props.row.device.groups?.map(g => g.id ?? "").filter(Boolean) ?? [],
    [props.row.device.groups],
  );
  const { warnings = [] } = props.row;
  const { data: statusData } = useStatusInfoById(deviceId, COCKPIT_DIAGNOSTICS, 1000);

  const { data: tellTaleData, isLoading: isLoadingTellTaleData } = useStatusInfoById(
    deviceId,
    TELLTALES_DIAGNOSTICS,
  );

  const fromDate = useMemo(
    () => props.fromDate ?? new Date(Date.now() - 60 * 60 * 1000 * 24 * 7),
    [props.fromDate],
  );
  const toDate = useMemo(() => props.toDate ?? new Date(Date.now()), [props.toDate]);
  const { data: faultData = [], isLoading: isLoadingFaultData } = useLatestFaultData(
    [deviceId],
    fromDate,
    toDate,
    undefined,
    10000,
    false,
  );

  const [selectedFault, setSelectedFault] = useState<FaultData | null>(null);

  const latitude = statusData?.latitude;
  const longitude = statusData?.longitude;
  const bearing = statusData?.bearing;
  const speed = statusData?.speed;
  const isDriving = statusData?.isDriving;
  const findSensorValue = (diagnosticId: string) =>
    // @ts-ignore
    statusData && statusData.statusData
      ? // @ts-ignore
        statusData.statusData.find(d => d.diagnostic.id === diagnosticId)?.data
      : 0;
  // width stuff neccessary to have the right width inside of geotab view
  const apiRef = useGridApiContext();

  const [width, setWidth] = useState(() => {
    const dimensions = apiRef.current.getRootDimensions();
    return dimensions?.viewportOuterSize.width;
  });

  const handleViewportInnerSizeChange = useCallback(() => {
    const dimensions = apiRef.current.getRootDimensions();
    setWidth(dimensions?.viewportOuterSize.width);
  }, [apiRef]);

  useEffect(() => {
    return apiRef.current.subscribeEvent("viewportInnerSizeChange", handleViewportInnerSizeChange);
  }, [apiRef, handleViewportInnerSizeChange]);

  const [tab, setTab] = useState(0);
  const [expandedPanelId, setExpandedPanelId] = useState<string | null>(null);
  const { t } = useTranslation();
  const { hasPermission: canDisplayMap } = useHasPermission("DisplayMap");
  const { panelOrder, savePanelOrder } = useRowDetailLayout(DEFAULT_PANEL_ORDER);

  const panels = useMemo(
    () => [
      {
        id: "cockpit",
        onExpand: () => setExpandedPanelId("cockpit"),
        content: (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
            }}
          >
            <Cockpit
              speed={speed ?? 0}
              isDriving={isDriving ?? false}
              rpm={
                props.row.isCharging === -1
                  ? findSensorValue("DiagnosticEngineSpeedId")
                  : findSensorValue("DiagnosticElectricVehicleBatteryPowerId")
              }
              adBlueLvl={findSensorValue("DiagnosticDieselExhaustFluidId")}
              outTemp={findSensorValue("DiagnosticOutsideTemperatureId")}
              fuelLvl={findSensorValue("DiagnosticFuelLevelId")}
              engineTemp={findSensorValue("DiagnosticEngineCoolantTemperatureId")}
              soc={findSensorValue("DiagnosticStateOfChargeId")}
              inTemp={findSensorValue("DiagnosticCabInteriorTemperatureId")}
              isCharging={props.row.isCharging}
              door1={findSensorValue("DiagnosticDoor1StatusId")}
              door2={findSensorValue("DiagnosticDoor2StatusId")}
              door3={findSensorValue("aMkXsw2rqV0-pxn4ko_qlnA")}
            />
          </Box>
        ),
      },
      {
        id: "map",
        onExpand: () => setExpandedPanelId("map"),
        isEmpty: !canDisplayMap,
        content: canDisplayMap ? (
          longitude && latitude ? (
            <MapComponent
              longitude={longitude}
              latitude={latitude}
              bearing={bearing ?? 0}
              speed={speed ?? 0}
              isDriving={isDriving ?? false}
            />
          ) : (
            <Skeleton variant="rectangular" width="100%" height="30vh" />
          )
        ) : (
          <Box />
        ),
      },
      {
        id: "tellTales",
        onExpand: () => setExpandedPanelId("tellTales"),
        content: (
          <Paper sx={{ p: "5px", height: "100%", boxSizing: "border-box" }}>
            {isLoadingTellTaleData ? (
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <CircularProgress />
              </Box>
            ) : (
              <TellTalesComponent
                displayTellTales={tellTales.filter(tt => !tt?.name?.includes("Reserved for"))}
                tellTaleData={(tellTaleData?.statusData ?? []) as StatusData[]}
              />
            )}
          </Paper>
        ),
      },
      {
        id: "tabs",
        onExpand: () => setExpandedPanelId("tabs"),
        content: (
          <Paper>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth">
              <Tab label={t("device")} />
              <Tab label={t("faultOverview.diagnostic")} />
              <Tab label={t("deviceHistory.title")} />
            </Tabs>

            {tab === 0 && (
              <Box sx={{ maxHeight: "30vh", overflow: "auto" }}>
                <CustomDeviceProperties deviceId={props?.row?.device?.id ?? ""} />
              </Box>
            )}

            {tab === 1 && (
              <Box sx={{ maxHeight: "30vh", overflow: "auto" }}>
                <CompactDiagnostics data={props?.row?.statusData?.statusData ?? []} />
              </Box>
            )}

            {tab === 2 && (
              <Box sx={{ maxHeight: "30vh", overflow: "auto" }}>
                <DeviceHistory
                  deviceId={props?.row?.device?.id ?? ""}
                  deviceName={props?.row?.device?.name}
                />
              </Box>
            )}
          </Paper>
        ),
        expandContent: (
          <Paper
            sx={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}
          >
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              variant="fullWidth"
              sx={{ flexShrink: 0 }}
            >
              <Tab label={t("device")} />
              <Tab label={t("faultOverview.diagnostic")} />
              <Tab label={t("deviceHistory.title")} />
            </Tabs>

            {tab === 0 && (
              <Box sx={{ flex: 1, overflow: "auto" }}>
                <CustomDeviceProperties deviceId={props?.row?.device?.id ?? ""} />
              </Box>
            )}

            {tab === 1 && (
              <Box sx={{ flex: 1, overflow: "auto" }}>
                <CompactDiagnostics data={props?.row?.statusData?.statusData ?? []} />
              </Box>
            )}

            {tab === 2 && (
              <Box sx={{ flex: 1, overflow: "hidden" }}>
                <DeviceHistory
                  deviceId={props?.row?.device?.id ?? ""}
                  deviceName={props?.row?.device?.name}
                  fullscreen
                />
              </Box>
            )}
          </Paper>
        ),
      },
      {
        id: "faultTable",
        onExpand: () => setExpandedPanelId("faultTable"),
        isEmpty: !isLoadingFaultData && faultData.length === 0,
        content: (
          <Paper>
            <FaultTable
              faultData={faultData}
              isLoading={isLoadingFaultData}
              setSelectedFault={setSelectedFault}
              deviceGroupIds={deviceGroupIds}
            />
          </Paper>
        ),
        expandContent: (
          <Paper sx={{ height: "100%" }}>
            <FaultTable
              faultData={faultData}
              isLoading={isLoadingFaultData}
              setSelectedFault={setSelectedFault}
              maxHeight="calc(80vh - 24px)"
              deviceGroupIds={deviceGroupIds}
            />
          </Paper>
        ),
      },
      {
        id: "faultInstructions",
        onExpand: () => setExpandedPanelId("faultInstructions"),
        isEmpty: warnings.length === 0 && !selectedFault,
        content: (
          <FaultInstructions
            warnings={warnings}
            selectedFault={selectedFault}
            setSelectedFault={setSelectedFault}
            deviceGroupIds={deviceGroupIds}
          />
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      speed,
      isDriving,
      props.row,
      canDisplayMap,
      longitude,
      latitude,
      bearing,
      isLoadingTellTaleData,
      tellTaleData,
      tab,
      faultData,
      isLoadingFaultData,
      selectedFault,
      warnings,
      t,
    ],
  );

  return (
    <Grid
      sx={{
        py: 2,
        height: "auto",
        boxSizing: "border-box",
        position: "sticky",
        left: 0,
        width,
        minHeight: "60vh",
      }}
      container
      spacing={1}
    >
      <Grid size={{ xs: 12, md: 4 }} style={{ margin: "auto" }}>
        <Box sx={{ height: "5vh", width: "100%" }}>
          <DeviceDetail row={props.row} />
        </Box>
      </Grid>
      <Grid size={{ xs: 12, md: 4 }} style={{ margin: "auto" }}>
        <Box sx={{ height: "5vh", width: "100%" }}>
          <GroupsDetail row={props.row} />
        </Box>
      </Grid>
      <Grid size={{ xs: 12, md: 4 }} style={{ margin: "auto" }}>
        <Box sx={{ height: "5vh", width: "100%" }} />
      </Grid>

      <DraggablePanelGrid panels={panels} panelOrder={panelOrder} onOrderChange={savePanelOrder} />

      <Dialog
        open={expandedPanelId !== null}
        onClose={() => setExpandedPanelId(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: "80vh" } }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            zIndex: 1,
          }}
        >
          <IconButton size="small" onClick={() => setExpandedPanelId(null)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: 1, height: "100%", overflow: "hidden" }}>
          {expandedPanelId &&
            (() => {
              const panel = panels.find(p => p.id === expandedPanelId);
              return panel?.expandContent ?? panel?.content;
            })()}
        </DialogContent>
      </Dialog>
    </Grid>
  );
});
export default RowDetailCockpit;
